/**
 * The VAT switch, as the sales screens need it.
 *
 * Four screens ask the same two questions - does this company charge VAT, and
 * at what rate does a new line start - and they are on screen at the same time
 * (the editor holds the line grid and the totals block, the detail page holds
 * the read-only grid and the same totals). One request answers all of them, so
 * the answer is cached at module level and shared: mounting three consumers
 * fires one GET, not three.
 *
 * The cache lives for the tab. That is the right lifetime: the setting is
 * edited on `/settings/invoicing`, which saves and then re-reads through the
 * same helper, so `refreshInvoicingVat` after a save is what keeps an open
 * document screen honest. Nothing else changes it.
 *
 * Failure is silent and falls back to VAT ON. An operator whose settings
 * request failed should see the column they saw yesterday, not a document that
 * has quietly stopped charging VAT.
 */
import { useEffect, useState } from "react";
import { getNumbering } from "../lib/sales";

export interface InvoicingVat {
  vatEnabled: boolean;
  /** What a new line starts at - zero while VAT is off. */
  defaultTaxRatePct: number;
}

/** What a screen assumes before the request answers, and if it never does. */
export const VAT_FALLBACK: InvoicingVat = { vatEnabled: true, defaultTaxRatePct: 24 };

let cached: InvoicingVat | null = null;
let inFlight: Promise<InvoicingVat> | null = null;

function load(): Promise<InvoicingVat> {
  if (cached) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = getNumbering()
      .then((cfg) => {
        // Read through `??`: this is JSON off the wire, and a browser holding a
        // cached bundle can outlive the server version it was built against. An
        // API that predates the switch answers with neither field, and for that
        // one the old behaviour - VAT on, at the stored rate - is correct.
        const value: InvoicingVat = {
          vatEnabled: cfg.vatEnabled ?? true,
          defaultTaxRatePct: cfg.effectiveTaxRatePct ?? cfg.defaultTaxRatePct ?? VAT_FALLBACK.defaultTaxRatePct,
        };
        cached = value;
        return value;
      })
      .catch(() => VAT_FALLBACK)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Drop the cache so the next screen re-reads it. Called after a settings save. */
export function refreshInvoicingVat(): void {
  cached = null;
}

export function useInvoicingVat(): InvoicingVat {
  const [value, setValue] = useState<InvoicingVat>(() => cached ?? VAT_FALLBACK);

  useEffect(() => {
    let alive = true;
    void load().then((v) => {
      if (alive) setValue(v);
    });
    return () => {
      alive = false;
    };
  }, []);

  return value;
}
